import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import ConfirmarModal from '../components/ConfirmarModal';
import FiscalRelatorioPrevia from '../components/FiscalRelatorioPrevia';
import '../styles/painelOperador.css';
import '../styles/gestaoBpoRh.css';
import '../styles/fiscal.css';
import '../styles/fiscalModerno.css';

// Fiscal → Entregas no Calendário (port do ZADRA). Uma entrega por empresa ativa e
// competência, espelhada num evento do Google Agenda da equipe. Mudar status ou excluir
// atualiza a Agenda primeiro; se ela recusar, nada muda no Saber. "Gerar mês" mostra
// antes o ensaio (o que seria criado) e só cria depois da confirmação.

const API = process.env.REACT_APP_API_BASE;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });
const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const DIAS_SEMANA = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
const STATUS = ['Pendente', 'Concluído', 'Atrasado com Obs'];

const semAcento = (s) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const fmtCnpj = (c) => (c && c.length === 14 ? c.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5') : c || '—');
const isoHoje = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const dataBr = (iso) => (iso ? iso.split('-').reverse().join('/') : '—');
const rotuloDia = (iso) => {
  const [a, m, d] = iso.split('-').map(Number);
  return `${DIAS_SEMANA[new Date(a, m - 1, d).getDay()]}, ${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}`;
};

function SeloStatus({ entrega, hoje }) {
  if (entrega.status === 'Concluído') return <span className="fz-selo ok"><i className="fas fa-circle-check"></i>Concluído</span>;
  if (entrega.status === 'Atrasado com Obs') return <span className="fz-selo alerta"><i className="fas fa-triangle-exclamation"></i>Atrasado com obs.</span>;
  if (entrega.data_prevista && entrega.data_prevista < hoje) return <span className="fz-selo erro"><i className="fas fa-circle-xmark"></i>Pendente — vencida</span>;
  return <span className="fz-selo neutro"><i className="fas fa-clock"></i>Pendente</span>;
}

export default function FiscalEntregas() {
  const agora = new Date();
  const [mes, setMes] = useState(agora.getMonth() + 1);
  const [ano, setAno] = useState(agora.getFullYear());
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');
  const [busca, setBusca] = useState('');
  const [responsavel, setResponsavel] = useState('');
  const [soMinhas, setSoMinhas] = useState(false);
  const [ocultarConcluidas, setOcultarConcluidas] = useState(false);
  const [agenda, setAgenda] = useState(null);
  const [geracao, setGeracao] = useState(null);   // { itens, total } do ensaio
  const [edicao, setEdicao] = useState(null);     // { entrega, status, data_real, observacao }
  const [excluir, setExcluir] = useState(null);
  const [processando, setProcessando] = useState(false);
  const hoje = isoHoje();

  const carregar = useCallback(() => {
    setCarregando(true);
    axios.get(`${API}/api/fiscal/entregas/`, { params: { mes, ano, _: Date.now() }, headers: headers() })
      .then((r) => { setDados(r.data); setErro(''); })
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível carregar as entregas'))
      .finally(() => setCarregando(false));
  }, [mes, ano]);
  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    axios.get(`${API}/api/fiscal/entregas/agenda/`, { params: { _: Date.now() }, headers: headers() })
      .then((r) => setAgenda(r.data.agenda))
      .catch(() => setAgenda({ conectado: false, mensagem: 'Não foi possível verificar a autorização do Google Agenda.' }));
  }, []);

  const responsaveis = useMemo(() => [...new Set((dados?.entregas || []).map((e) => e.responsavel).filter(Boolean))].sort(), [dados]);

  const filtradas = useMemo(() => {
    if (!dados) return [];
    const termo = semAcento(busca.trim());
    return dados.entregas.filter((e) => {
      if (ocultarConcluidas && e.status === 'Concluído') return false;
      if (soMinhas && e.responsavel_user_id !== dados.meu_user_id) return false;
      if (responsavel && e.responsavel !== responsavel) return false;
      return !termo || semAcento(e.cliente).includes(termo) || String(e.cod) === termo;
    });
  }, [dados, busca, ocultarConcluidas, soMinhas, responsavel]);

  const porDia = useMemo(() => {
    const grupos = {};
    filtradas.forEach((e) => { (grupos[e.data_prevista || 'sem-data'] = grupos[e.data_prevista || 'sem-data'] || []).push(e); });
    return Object.keys(grupos).sort().map((dia) => ({ dia, itens: grupos[dia] }));
  }, [filtradas]);

  const resumo = useMemo(() => {
    const lista = dados?.entregas || [];
    return {
      total: lista.length,
      concluidas: lista.filter((e) => e.status === 'Concluído').length,
      vencidas: lista.filter((e) => e.status === 'Pendente' && e.data_prevista && e.data_prevista < hoje).length,
    };
  }, [dados, hoje]);

  // ---- gerar mês: ensaio -> confirmação -> criação ----
  const ensaiarGeracao = () => {
    setProcessando(true); setErro(''); setMsg('');
    axios.post(`${API}/api/fiscal/entregas/gerar/`, { mes, ano, ensaio: true }, { headers: headers() })
      .then((r) => setGeracao({ itens: r.data.itens, total: r.data.total }))
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível preparar a geração'))
      .finally(() => setProcessando(false));
  };
  const confirmarGeracao = () => {
    setProcessando(true);
    axios.post(`${API}/api/fiscal/entregas/gerar/`, { mes, ano, ensaio: false }, { headers: headers(), timeout: 600000 })
      .then((r) => { setMsg(r.data.mensagem); setGeracao(null); carregar(); })
      .catch((e) => { setErro(e.response?.data?.mensagem || 'Não foi possível gerar o mês'); setGeracao(null); carregar(); })
      .finally(() => setProcessando(false));
  };

  // ---- status ----
  const abrirEdicao = (e) => setEdicao({
    entrega: e, status: e.status === 'Pendente' ? 'Concluído' : e.status, data_real: e.data_real || hoje, observacao: e.observacao || '',
  });
  const salvarEdicao = () => {
    setProcessando(true);
    const { entrega, ...corpo } = edicao;
    axios.patch(`${API}/api/fiscal/entregas/${entrega.id}/`, corpo, { headers: headers() })
      .then((r) => {
        const x = r.data.entrega;
        setDados((d) => ({ ...d, entregas: d.entregas.map((e) => (e.id === entrega.id ? { ...e, status: x.status, data_real: x.data_real, observacao: x.observacao } : e)) }));
        setMsg(`${entrega.cliente}: ${x.status}`); setEdicao(null);
      })
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível salvar'))
      .finally(() => setProcessando(false));
  };
  const confirmarExclusao = () => {
    setProcessando(true);
    axios.delete(`${API}/api/fiscal/entregas/${excluir.id}/`, { headers: headers() })
      .then((r) => { setMsg(r.data.mensagem); setDados((d) => ({ ...d, entregas: d.entregas.filter((e) => e.id !== excluir.id) })); setExcluir(null); })
      .catch((e) => { setErro(e.response?.data?.mensagem || 'Não foi possível excluir'); setExcluir(null); })
      .finally(() => setProcessando(false));
  };

  // "Gerar mês" grava todas as empresas ativas na agenda Prazos Fiscais pela conta central:
  // sem ela conectada (Fiscal → Configurações / Usuários), bloqueia
  const agendaIndisponivel = !!agenda && !agenda.conectado;
  const anos = [ano - 1, ano, ano + 1];

  return (
    <div className="painel-op fz-app">
      <div className="container-fluid mt-4 shadow p-4 rounded bg-white">
        <div className="header-wrapper d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="d-flex align-items-center">
            <i className="fas fa-calendar-check header-icon me-2" aria-hidden="true"></i>
            <h2 className="titulo-pagina m-0">Entregas no Calendário</h2>
          </div>
          <div className="gr-toolbar">
            <FiscalRelatorioPrevia />
            <button type="button" className="gr-btn" onClick={carregar} disabled={carregando}>
              <i className={`fas ${carregando ? 'fa-spinner fa-spin' : 'fa-rotate'}`} aria-hidden="true"></i>Atualizar
            </button>
            <button type="button" className="gr-btn primaria" onClick={ensaiarGeracao} disabled={processando || agendaIndisponivel}
              title="Mostra antes quais entregas seriam criadas nesta competência">
              <i className={`fas ${processando && !geracao ? 'fa-spinner fa-spin' : 'fa-calendar-plus'}`} aria-hidden="true"></i>Gerar mês
            </button>
          </div>
        </div>

        {agendaIndisponivel && (
          <div className="alert alert-warning py-2 small">
            <i className="fas fa-triangle-exclamation me-2"></i>{agenda.mensagem}{' '}
            <a href="/Fiscal/Configuracoes" className="fw-semibold">Abrir Configurações</a>
          </div>
        )}
        {agenda?.conectado && (
          <div className="alert alert-light border py-2 small">
            <i className="fas fa-calendar-check me-2 text-primary"></i>
            "Gerar mês" cria os prazos das <b>{agenda.empresas_ativas}</b> empresa(s) ativas na agenda <b>Prazos Fiscais</b>, pela conta de <b>{agenda.operador_nome}</b> ({agenda.conta}).
          </div>
        )}
        {erro && <div className="alert alert-danger py-2" role="button" onClick={() => setErro('')}><i className="fas fa-circle-exclamation me-2"></i>{erro}</div>}
        {msg && <div className="alert alert-success py-2" role="button" onClick={() => setMsg('')}><i className="fas fa-circle-check me-2"></i>{msg}</div>}

        <div className="fz-filtros">
          <select className="fz-sel" value={mes} onChange={(e) => setMes(Number(e.target.value))} aria-label="Mês">
            {MESES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
          <select className="fz-sel" value={ano} onChange={(e) => setAno(Number(e.target.value))} aria-label="Ano">
            {anos.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <input className="po-busca" placeholder="Buscar por nome ou código" value={busca} onChange={(e) => setBusca(e.target.value)} />
          <select className="fz-sel" value={responsavel} onChange={(e) => setResponsavel(e.target.value)} aria-label="Responsável">
            <option value="">Todos os responsáveis</option>
            {responsaveis.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <label className="po-toggle"><input type="checkbox" checked={soMinhas} onChange={(e) => setSoMinhas(e.target.checked)} />Só as minhas</label>
          <label className="po-toggle"><input type="checkbox" checked={ocultarConcluidas} onChange={(e) => setOcultarConcluidas(e.target.checked)} />Ocultar concluídas</label>
        </div>

        <div className="d-flex flex-wrap gap-2 mb-3">
          <span className="fz-selo neutro">Total: <span className="fz-num">{resumo.total}</span></span>
          <span className="fz-selo ok">Concluídas: <span className="fz-num">{resumo.concluidas}</span></span>
          <span className={`fz-selo ${resumo.vencidas ? 'erro' : 'neutro'}`}>Vencidas pendentes: <span className="fz-num">{resumo.vencidas}</span></span>
        </div>

        {carregando && !dados ? (
          <div className="text-center py-5 text-muted"><i className="fas fa-spinner fa-spin fa-3x mb-3" style={{ color: '#00b5cc' }}></i><div className="fs-5">Carregando entregas...</div></div>
        ) : porDia.length === 0 ? (
          <div className="text-center text-muted py-5">
            {dados?.entregas?.length ? 'Nenhuma entrega com esses filtros.' : `Nenhuma entrega em ${MESES[mes - 1]}/${ano}. Use "Gerar mês" para criar.`}
          </div>
        ) : (
          <div className="po-scroll" style={{ maxHeight: 'none', overflow: 'visible' }}>
            {porDia.map(({ dia, itens }) => (
              <section className="po-card po-enter mb-3" key={dia}>
                <header className="po-card-head">
                  <h3 className={dia !== 'sem-data' && dia < hoje ? 'text-danger' : undefined}>
                    <i className="fas fa-calendar-day" aria-hidden="true"></i>{dia === 'sem-data' ? 'Sem data' : rotuloDia(dia)}{dia === hoje && ' · hoje'}
                  </h3>
                  <span className="badge bg-light text-dark">{itens.length}</span>
                </header>
                <div className="po-card-body p-0">
                  <table className="table table-hover po-table align-middle mb-0">
                    <tbody>
                      {itens.map((e) => (
                        <tr key={e.id}>
                          <td style={{ width: 70 }} className="fz-num">{e.cod}</td>
                          <td>
                            <div className="fw-semibold">{e.cliente}</div>
                            <div className="fz-sub fz-mono">{fmtCnpj(e.cnpj)}</div>
                          </td>
                          <td className="small" style={{ width: 160 }}>
                            {e.responsavel || <span className="text-muted">—</span>}
                          </td>
                          <td style={{ width: 200 }}>
                            <SeloStatus entrega={e} hoje={hoje} />
                            {e.data_real && <div className="fz-sub">feita em {dataBr(e.data_real)}</div>}
                            {e.observacao && <div className="fz-sub text-truncate" style={{ maxWidth: 190 }} title={e.observacao}><i className="fas fa-note-sticky me-1"></i>{e.observacao}</div>}
                          </td>
                          <td className="text-end" style={{ whiteSpace: 'nowrap', width: 100 }}>
                            <button className="btn btn-sm btn-outline-primary me-1" title={e.tem_evento ? 'Mudar status' : 'Sem evento na Agenda'}
                              disabled={!e.tem_evento} onClick={() => abrirEdicao(e)}><i className="fas fa-pen"></i></button>
                            <button className="btn btn-sm btn-outline-danger" title="Excluir do calendário" onClick={() => setExcluir(e)}><i className="fas fa-trash"></i></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            ))}
          </div>
        )}

        {geracao && (
          <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,.45)' }}>
            <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
              <div className="modal-content shadow-lg">
                <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                  <div>
                    <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}><i className="fas fa-calendar-plus me-2" style={{ color: '#00b5cc' }}></i>Gerar {MESES[mes - 1]}/{ano}</h5>
                    <small style={{ color: '#6c757d' }}>Ensaio — empresas ativas que ainda não têm entrega nesta competência; nada foi criado ainda.</small>
                  </div>
                  <button type="button" className="btn-close" onClick={() => setGeracao(null)} disabled={processando}></button>
                </div>
                <div className="modal-body">
                  {geracao.total === 0 ? (
                    <div className="text-center text-muted py-3">
                      Todas as suas empresas ativas já têm entrega nesta competência (ou foram excluídas dela). Nada a criar.
                    </div>
                  ) : (
                    <>
                      <p className="mb-2">Serão criados <b className="fz-num">{geracao.total}</b> prazo(s), com o evento na agenda <b>Prazos Fiscais</b> (conta de {agenda?.operador_nome}):</p>
                      <table className="table table-sm po-table mb-0">
                        <thead><tr><th>Data</th><th>Cód.</th><th>Empresa</th><th>Responsável</th><th className="text-center">Dia da regra</th></tr></thead>
                        <tbody>
                          {geracao.itens.map((i) => (
                            <tr key={i.cod}><td className="fz-num">{dataBr(i.data_prevista)}</td><td className="fz-num">{i.cod}</td><td>{i.cliente}</td><td className="small">{i.responsavel || '—'}</td><td className="text-center fz-num">{i.entrega_dia || 'último'}</td></tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}
                </div>
                <div className="modal-footer bg-light">
                  <button className="btn btn-secondary me-auto" onClick={() => setGeracao(null)} disabled={processando}><i className="fas fa-times me-2"></i>Cancelar</button>
                  {geracao.total > 0 && (
                    <button className="btn btn-primary" onClick={confirmarGeracao} disabled={processando}>
                      <i className={`fas ${processando ? 'fa-spinner fa-spin' : 'fa-check'} me-2`}></i>{processando ? 'Criando...' : `Criar ${geracao.total} entrega(s)`}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {edicao && (
          <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,.45)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content shadow-lg">
                <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                  <div>
                    <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}><i className="fas fa-pen me-2" style={{ color: '#00b5cc' }}></i>Status da entrega</h5>
                    <small style={{ color: '#6c757d' }}>{edicao.entrega.cod} · {edicao.entrega.cliente} · prevista {dataBr(edicao.entrega.data_prevista)}</small>
                  </div>
                  <button type="button" className="btn-close" onClick={() => setEdicao(null)} disabled={processando}></button>
                </div>
                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label small fw-bold mb-1">Status</label>
                      <select className="form-select" value={edicao.status} onChange={(ev) => setEdicao((f) => ({ ...f, status: ev.target.value }))}>
                        {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-bold mb-1">Data real da execução</label>
                      <input type="date" className="form-control" value={edicao.data_real} onChange={(ev) => setEdicao((f) => ({ ...f, data_real: ev.target.value }))} />
                    </div>
                    <div className="col-12">
                      <label className="form-label small fw-bold mb-1">Observação {edicao.status === 'Atrasado com Obs' && <span className="text-danger">(explique o atraso)</span>}</label>
                      <textarea className="form-control" rows={3} value={edicao.observacao} onChange={(ev) => setEdicao((f) => ({ ...f, observacao: ev.target.value }))} />
                    </div>
                  </div>
                  <div className="form-text mt-2">A cor, o título e a descrição do evento no Google Agenda são atualizados junto.</div>
                </div>
                <div className="modal-footer bg-light">
                  <button className="btn btn-secondary me-auto" onClick={() => setEdicao(null)} disabled={processando}><i className="fas fa-times me-2"></i>Cancelar</button>
                  <button className="btn btn-primary" onClick={salvarEdicao}
                    disabled={processando || !edicao.data_real || (edicao.status === 'Atrasado com Obs' && !edicao.observacao.trim())}>
                    <i className={`fas ${processando ? 'fa-spinner fa-spin' : 'fa-floppy-disk'} me-2`}></i>{processando ? 'Salvando...' : 'Salvar'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <ConfirmarModal aberto={!!excluir} perigo titulo="Excluir entrega" rotuloConfirmar="Excluir" processando={processando}
          texto={excluir && <>Excluir a entrega de <b>{excluir.cliente}</b> em {MESES[mes - 1]}/{ano}? O evento é apagado do Google Agenda e o <b>Gerar mês</b> não recria esta empresa nesta competência.</>}
          onConfirmar={confirmarExclusao} onCancelar={() => setExcluir(null)} />
      </div>
    </div>
  );
}
